/**
 * Wallet service — Core wallet operations.
 *
 * BIP-39 mnemonic generation + validation, BIP-44 key derivation,
 * balance fetching, transaction signing + broadcasting, token management.
 */

import { ethers } from 'ethers';
import { encrypt, decrypt } from './crypto.js';
import { saveVault, loadVault, hasVault, saveTx, updateTxStatus, getPendingTransactions } from './storage.js';
import { getChainConfig, ERC20_ABI } from '@tokenstreaming/shared';
import type { EncryptedVault, TokenBalance, TxRecord } from './types.js';

const BIP44_PATH = "m/44'/60'/0'/0/0";
const BALANCE_POLL_INTERVAL = 15_000; // 15 seconds
const TX_CONFIRM_POLL_INTERVAL = 3_000; // 3 seconds

export class WalletService {
  private wallet: ethers.HDNodeWallet | null = null;
  private provider: ethers.JsonRpcProvider | null = null;
  private chainId: string;
  private balancePollTimer: ReturnType<typeof setInterval> | null = null;
  private pendingTxPollTimer: ReturnType<typeof setInterval> | null = null;

  constructor(chainId: string = 'eip155:56') {
    this.chainId = chainId;
    this.initProvider();
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  async isInitialized(): Promise<boolean> {
    return hasVault();
  }

  async create(password: string): Promise<{ mnemonic: string; address: string }> {
    const randomWallet = ethers.Wallet.createRandom();
    const mnemonic = randomWallet.mnemonic?.phrase;
    if (!mnemonic) throw new WalletServiceError('CREATE_FAILED', 'Failed to generate mnemonic');

    const hdWallet = ethers.HDNodeWallet.fromMnemonic(
      ethers.Mnemonic.fromPhrase(mnemonic),
      BIP44_PATH
    );

    // Encrypt and store
    const encrypted = await encrypt(mnemonic, password);
    const vault: EncryptedVault = {
      ciphertext: encrypted.ciphertext,
      iv: encrypted.iv,
      salt: encrypted.salt,
      version: 1,
    };
    await saveVault(vault, hdWallet.address);

    this.wallet = hdWallet;
    this.connectProvider();
    this.startPolling();

    return { mnemonic, address: hdWallet.address };
  }

  async import(mnemonic: string, password: string): Promise<{ address: string }> {
    // Validate mnemonic
    if (!ethers.Mnemonic.isValidMnemonic(mnemonic)) {
      throw new WalletServiceError('INVALID_MNEMONIC', 'Invalid mnemonic phrase');
    }

    const hdWallet = ethers.HDNodeWallet.fromMnemonic(
      ethers.Mnemonic.fromPhrase(mnemonic),
      BIP44_PATH
    );

    // Encrypt and store
    const encrypted = await encrypt(mnemonic, password);
    const vault: EncryptedVault = {
      ciphertext: encrypted.ciphertext,
      iv: encrypted.iv,
      salt: encrypted.salt,
      version: 1,
    };
    await saveVault(vault, hdWallet.address);

    this.wallet = hdWallet;
    this.connectProvider();
    this.startPolling();

    return { address: hdWallet.address };
  }

  async unlock(password: string): Promise<string> {
    const stored = await loadVault();
    if (!stored) {
      throw new WalletServiceError('NOT_INITIALIZED', 'No wallet found. Create or import one first.');
    }

    const mnemonic = await decrypt(
      stored.vault.ciphertext,
      password,
      stored.vault.iv,
      stored.vault.salt
    );

    const hdWallet = ethers.HDNodeWallet.fromMnemonic(
      ethers.Mnemonic.fromPhrase(mnemonic),
      BIP44_PATH
    );

    this.wallet = hdWallet;
    this.connectProvider();
    this.startPolling();

    return hdWallet.address;
  }

  lock(): void {
    this.wallet = null;
    this.stopPolling();
  }

  isUnlocked(): boolean {
    return this.wallet !== null;
  }

  getAddress(): string | null {
    return this.wallet?.address ?? null;
  }

  getPrivateKey(): string | null {
    return this.wallet?.privateKey ?? null;
  }

  // ─── Chain management ─────────────────────────────────────────────────────

  switchChain(chainId: string): void {
    this.chainId = chainId;
    this.initProvider();
    this.connectProvider();
  }

  getChainId(): string {
    return this.chainId;
  }

  getProvider(): ethers.JsonRpcProvider | null {
    return this.provider;
  }

  // ─── Balance ──────────────────────────────────────────────────────────────

  async getNativeBalance(): Promise<bigint> {
    this.requireUnlocked();
    if (!this.provider) throw new WalletServiceError('NO_PROVIDER', 'Provider not connected');
    const balance = await this.provider.getBalance(this.wallet!.address);
    return balance;
  }

  async getTokenBalances(): Promise<TokenBalance[]> {
    this.requireUnlocked();
    if (!this.provider) return [];

    const chainConfig = getChainConfig(this.chainId);
    const balances: TokenBalance[] = [];

    for (const token of chainConfig.tokens) {
      try {
        const contract = new ethers.Contract(token.address, ERC20_ABI, this.provider);
        const balance = await contract.balanceOf!(this.wallet!.address) as bigint;
        balances.push({
          address: token.address,
          symbol: token.symbol,
          name: token.name,
          decimals: token.decimals,
          balance,
        });
      } catch {
        // Token contract call failed — include with zero balance
        balances.push({
          address: token.address,
          symbol: token.symbol,
          name: token.name,
          decimals: token.decimals,
          balance: 0n,
        });
      }
    }

    return balances;
  }

  // ─── Transactions ─────────────────────────────────────────────────────────

  async sendNative(to: string, amount: bigint): Promise<string> {
    this.requireUnlocked();
    const signer = this.getSigner();

    const tx = await signer.sendTransaction({
      to,
      value: amount,
    });

    await this.recordTx(tx.hash, this.wallet!.address, to, amount.toString(), 'BNB', 'send');
    this.watchTxConfirmation(tx.hash);

    return tx.hash;
  }

  async sendToken(tokenAddress: string, to: string, amount: bigint): Promise<string> {
    this.requireUnlocked();
    const signer = this.getSigner();

    const contract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
    const symbol = await contract.symbol!() as string;

    const tx = await contract.transfer!(to, amount) as ethers.TransactionResponse;

    await this.recordTx(tx.hash, this.wallet!.address, to, amount.toString(), symbol, 'send');
    this.watchTxConfirmation(tx.hash);

    return tx.hash;
  }

  async approveToken(tokenAddress: string, spender: string, amount: bigint): Promise<string> {
    this.requireUnlocked();
    const signer = this.getSigner();

    const contract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
    const tx = await contract.approve!(spender, amount) as ethers.TransactionResponse;

    await this.recordTx(tx.hash, this.wallet!.address, spender, amount.toString(), 'APPROVE', 'approve');
    this.watchTxConfirmation(tx.hash);

    return tx.hash;
  }

  async getAllowance(tokenAddress: string, spender: string): Promise<bigint> {
    this.requireUnlocked();
    if (!this.provider) return 0n;

    const contract = new ethers.Contract(tokenAddress, ERC20_ABI, this.provider);
    return contract.allowance!(this.wallet!.address, spender) as Promise<bigint>;
  }

  // ─── Signing ──────────────────────────────────────────────────────────────

  async signMessage(message: string): Promise<string> {
    this.requireUnlocked();
    return this.wallet!.signMessage(message);
  }

  async signBytes(data: Uint8Array): Promise<string> {
    this.requireUnlocked();
    return this.wallet!.signMessage(data);
  }

  // ─── Gas estimation ───────────────────────────────────────────────────────

  async estimateGas(to: string, value: bigint, data?: string): Promise<bigint> {
    if (!this.provider) throw new WalletServiceError('NO_PROVIDER', 'Provider not connected');
    return this.provider.estimateGas({
      from: this.wallet?.address,
      to,
      value,
      data,
    });
  }

  async getGasPrice(): Promise<bigint> {
    if (!this.provider) throw new WalletServiceError('NO_PROVIDER', 'Provider not connected');
    const feeData = await this.provider.getFeeData();
    return feeData.gasPrice ?? 5000000000n; // 5 gwei fallback for BSC
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  private initProvider(): void {
    const chainConfig = getChainConfig(this.chainId);
    const rpcUrl = chainConfig.rpcUrls[0];
    if (!rpcUrl) throw new WalletServiceError('NO_RPC', `No RPC URL for chain ${this.chainId}`);
    this.provider = new ethers.JsonRpcProvider(rpcUrl);
  }

  private connectProvider(): void {
    if (this.wallet && this.provider) {
      this.wallet = this.wallet.connect(this.provider) as ethers.HDNodeWallet;
    }
  }

  private getSigner(): ethers.HDNodeWallet {
    if (!this.wallet) throw new WalletServiceError('LOCKED', 'Wallet is locked');
    if (!this.provider) throw new WalletServiceError('NO_PROVIDER', 'Provider not connected');
    return this.wallet;
  }

  private requireUnlocked(): void {
    if (!this.wallet) throw new WalletServiceError('LOCKED', 'Wallet is locked');
  }

  private startPolling(): void {
    this.stopPolling();
    // Poll pending tx confirmations
    this.pendingTxPollTimer = setInterval(() => {
      void this.checkPendingTransactions();
    }, TX_CONFIRM_POLL_INTERVAL);
  }

  private stopPolling(): void {
    if (this.balancePollTimer) {
      clearInterval(this.balancePollTimer);
      this.balancePollTimer = null;
    }
    if (this.pendingTxPollTimer) {
      clearInterval(this.pendingTxPollTimer);
      this.pendingTxPollTimer = null;
    }
  }

  private async checkPendingTransactions(): Promise<void> {
    if (!this.provider) return;
    const pending = await getPendingTransactions();
    for (const tx of pending) {
      try {
        const receipt = await this.provider.getTransactionReceipt(tx.hash);
        if (receipt) {
          const status = receipt.status === 1 ? 'confirmed' : 'failed';
          await updateTxStatus(tx.hash, status);
        }
      } catch {
        // Network error — will retry next poll
      }
    }
  }

  private async recordTx(
    hash: string,
    from: string,
    to: string,
    amount: string,
    tokenSymbol: string,
    type: TxRecord['type']
  ): Promise<void> {
    await saveTx({
      hash,
      from,
      to,
      amount,
      token: '',
      tokenSymbol,
      timestamp: Date.now(),
      status: 'pending',
      chainId: this.chainId,
      type,
    });
  }

  private watchTxConfirmation(hash: string): void {
    if (!this.provider) return;
    // Single-shot wait with timeout
    this.provider.waitForTransaction(hash, 1, 120_000)
      .then(receipt => {
        if (receipt) {
          const status = receipt.status === 1 ? 'confirmed' : 'failed';
          void updateTxStatus(hash, status);
        }
      })
      .catch(() => {
        // Timeout or error — will be picked up by poll
      });
  }
}

export class WalletServiceError extends Error {
  constructor(
    public readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'WalletServiceError';
  }
}

// Singleton instance
let walletServiceInstance: WalletService | null = null;

export function getWalletService(chainId?: string): WalletService {
  if (!walletServiceInstance) {
    walletServiceInstance = new WalletService(chainId);
  }
  return walletServiceInstance;
}
